import moment from 'moment';
import { connect } from 'react-redux';
import { compose, withHandlers, withProps, withStateHandlers } from 'recompose';
import { Form } from 'antd';

import { closeCurrentModal } from '../modal-reducer';
import { MeetingNotesModal as Component } from './component';
import { handleCreateMeetingNote, handleUpdateMeetingNote } from '../../redux/thunk/meeting-notes';
import { getItemById, getItemsById, getItemByPath } from '../../utils';

/**
 * Merge date and time moments into a single date
 *
 * getMeetingDateByDateAndTime :: (Moment, Moment) -> Date
 */
const getMeetingDateByDateAndTime = (date, time) =>
  moment(date)
    .set('hours', time.hours())
    .set('minutes', time.minutes())
    .set('seconds', 0)
    .set('milliseconds', 0)
    .toDate();

/**
 * Returns a meeting note object based on the component props (meetingNoteId).
 * It will generate a default document when the contactId prop present (need to create a new note)
 * In other way - it will find a meeting note by id in the state
 *
 * getMeetingNote :: (Object, Object) -> Object
 */
const getMeetingNote = (state, { requestId, meetingNoteId }) => {
  const {
    requests = {}, meetingNotes = {}, contacts = {}, organizationSettings = {},
  } = state;

  if (requestId && meetingNoteId) {
    const request = getItemById(requestId, requests);
    const requestContacts = getItemsById(request.contacts, contacts);
    const meetingNote = getItemById(meetingNoteId, meetingNotes);
    const requestSource = getItemByPath(
      ['items', 'requestSources', request.source],
      organizationSettings,
    );

    return {
      ...meetingNote,
      request: {
        ...request,
        contacts: requestContacts.map(({ fullName }) => fullName),
        source: requestSource,
      },
    };
  }
  if (requestId) {
    const request = getItemById(requestId, requests);
    const requestContacts = getItemsById(request.contacts, contacts);

    const requestSource = getItemByPath(
      ['items', 'requestSources', request.source],
      organizationSettings,
    );

    return {
      request: {
        ...request,
        contacts: requestContacts.map(({ fullName }) => fullName),
        source: requestSource,
      },
      requestId: request._id,
      meetingDate: new Date(),
    };
  }

  const meetingNote = getItemById(meetingNoteId, meetingNotes);
  const request = getItemById(meetingNote.request, requests);
  const requestContacts = getItemsById(request.contacts, contacts);
  const requestSource = getItemByPath(
    ['items', 'requestSources', request.source],
    organizationSettings,
  );

  return {
    ...meetingNote,
    request: {
      ...request,
      contacts: requestContacts.map(({ fullName }) => fullName),
      source: requestSource,
    },
  };
};

const mapStateToProps = (state, { payload }) => {
  const { meetingNotes = {} } = state;
  return {
    isLoading: meetingNotes.isCreating || meetingNotes.isUpdating || false,
    // set meeting note in common form
    meetingNote: getMeetingNote(state, payload),
  };
};

export const MeetingNotesModal = Form.create()(compose(
  connect(
    mapStateToProps,
    {
      closeCurrentModal,
      handleCreateMeetingNote,
      handleUpdateMeetingNote,
    },
  ),

  withProps(({ meetingNote }) => ({
    title: meetingNote._id ? 'Update Meeting Note' : 'Add Meeting Note',
  })),

  withStateHandlers(
    ({ meetingNote: { text = '', meetingDate = new Date(), meetingInfo = '' } }) => ({
      text,
      // convert to moment object because Ant date and time picker are accept only moment
      date: moment(meetingDate),
      time: moment(meetingDate),
      meetingInfo,
    }),
    {
      setMeetingDate: () => date => ({ date }),
      setMeetingTime: () => time => ({ time }),
      setMeetingText: () => text => ({ text }),
      setMeetingInfo: () => meetingInfo => ({ meetingInfo }),
    },
  ),

  withHandlers({
    onTextChange: ({ setMeetingText }) => (event) => {
      setMeetingText(event);
    },

    onMeetingInfoChange: ({ setMeetingInfo }) => (event) => {
      setMeetingInfo(event);
    },

    onSave: ({
      date, time, text, meetingNote, meetingInfo, form, ...actions
    }) => (event) => {
      event.preventDefault();
      form.validateFieldsAndScroll((err) => {
        if (!err) {
          const isUpdating = !!meetingNote._id;

          // compose date and time objects into a single meeting date
          const meetingDate = getMeetingDateByDateAndTime(date, time);

          const res = isUpdating
            ? actions.handleUpdateMeetingNote(meetingNote._id, { text, meetingDate, meetingInfo })
            : actions.handleCreateMeetingNote({
              text,
              meetingDate,
              requestId: meetingNote.requestId,
            });

          res.then(() => actions.closeCurrentModal());
        } else {
          console.error(err);
        }
      });
    },
  }),
)(Component));



// WEBPACK FOOTER //
// ./src/modals/meeting-notes-modal/container.js